import * as anchor from "@project-serum/anchor";
import { programs } from "@metaplex/js";
import idl from "./idl.json";
import { getMetadataAccount } from "./metadata";
import { testWallet } from "@config/config";
import tokenMap from "./tokenmap.json";
import { useStakedBonus } from "../components/partials/StakePage/useBonus";
import { seekerAddresses } from "@config/seeker";
import stakedTokenInfo from "./stakedTokens.json";
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, getAssociatedTokenAddress, createAssociatedTokenAccountInstruction } from "@solana/spl-token";
import { PublicKey, SystemProgram, Keypair, SYSVAR_CLOCK_PUBKEY, LAMPORTS_PER_SOL, Transaction, sendAndConfirmTransaction } from '@solana/web3.js';
import { findProgramAddressSync } from "@project-serum/anchor/dist/cjs/utils/pubkey";

const {
  metadata: { Metadata },
} = programs;

const programID = new PublicKey(idl.metadata.address);
const TRANSIENT_NFT_STAKE_SEED_PREFIX = "transient";

export const stake = async (provider, mint) => {
  const program = new anchor.Program(idl, programID, provider);

  try {
    const tokenAccount = await getAssociatedTokenAddress(
      mint,
      provider.wallet.publicKey,
      false,
      TOKEN_PROGRAM_ID,
      ASSOCIATED_TOKEN_PROGRAM_ID
    );

    const tokenAccountInfo = await provider.connection.getAccountInfo(tokenAccount);

    const transaction = new Transaction();

    // Create the associated token account if it doesn't exist
    if (!tokenAccountInfo) {
      transaction.add(
        createAssociatedTokenAccountInstruction(
          provider.wallet.publicKey,
          tokenAccount,
          provider.wallet.publicKey,
          mint
        )
      );
    }

    const metadata = await Metadata.getPDA(mint);
    const stakeAccount = Keypair.generate();

    transaction.add(
      program.instruction.stake({
        accounts: {
          stakeAccount: stakeAccount.publicKey,
          mint,
          metadata,
          tokenAccount,
          user: provider.wallet.publicKey,
          clock: SYSVAR_CLOCK_PUBKEY,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: SystemProgram.programId,
        },
      })
    );

    const latestBlockHash = await provider.connection.getLatestBlockhash();
    transaction.lastValidBlockHeight = latestBlockHash.lastValidBlockHeight;
    transaction.recentBlockhash = latestBlockHash.blockhash;
    transaction.feePayer = provider.wallet.publicKey;
    transaction.partialSign(stakeAccount);
    const signedTransaction = await provider.wallet.signTransaction(transaction);
    const signature = await provider.connection.sendRawTransaction(signedTransaction.serialize(), {
      skipPreflight: true,
      maxRetries: 2,
    });

    const timeout = 60000; // 60 seconds
    const confirmationPromise = provider.connection.confirmTransaction({
      signature,
      blockhash: latestBlockHash.blockhash,
      lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
    }, 'confirmed'); // Set the commitment level to 'confirmed'

    const confirmation = await Promise.race([
      confirmationPromise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Transaction confirmation timeout')), timeout)
      )
    ]);

    if (confirmation.value.err) {
      console.error("Transaction confirmation error: ", confirmation.value.err);
      throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
    }
  } catch (err) {
    console.log("Transaction error while stake: ", err);
    throw err;
  }
};

export const unstake = async (provider, mint, stakeAccount) => {
  const program = new anchor.Program(idl, programID, provider);

  try {
    const tokenAccount = await getAssociatedTokenAddress(
      mint,
      provider.wallet.publicKey,
      false,
      TOKEN_PROGRAM_ID,
      ASSOCIATED_TOKEN_PROGRAM_ID
    );

    const [pda] = findProgramAddressSync(
      [
        anchor.utils.bytes.utf8.encode(TRANSIENT_NFT_STAKE_SEED_PREFIX),
        provider.wallet.publicKey.toBytes(),
        mint.toBytes(),
      ],
      program.programId
    );

    const transaction = new Transaction().add(
      program.instruction.unstake({
        accounts: {
          stakeAccount,
          tokenAccount,
          authority: pda,
          user: provider.wallet.publicKey,
          systemProgram: SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
        },
      })
    );

    const latestBlockHash = await provider.connection.getLatestBlockhash();
    transaction.lastValidBlockHeight = latestBlockHash.lastValidBlockHeight;
    transaction.recentBlockhash = latestBlockHash.blockhash;
    transaction.feePayer = provider.wallet.publicKey;

    const signedTransaction = await provider.wallet.signTransaction(transaction);
    const signature = await provider.connection.sendRawTransaction(signedTransaction.serialize(), {
      skipPreflight: true,
      maxRetries: 2,
    });

    const timeout = 60000; // 60 seconds
    const confirmationPromise = provider.connection.confirmTransaction({
      signature,
      blockhash: latestBlockHash.blockhash,
      lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
    }, 'confirmed'); // Set the commitment level to 'confirmed'

    const confirmation = await Promise.race([
      confirmationPromise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Transaction confirmation timeout')), timeout)
      )
    ]);

    if (confirmation.value.err) {
      console.error("Transaction confirmation error: ", confirmation.value.err);
      throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
    }
  } catch (err) {
    console.log("Transaction error while unstake: ", err);
    throw err;
  }
};

export const getReclaimableList = async provider => {
  try {
    const program = new anchor.Program(idl, programID, provider)
    const accounts = await program.account.stakeItem.all([
      {
        memcmp: {
          offset: 72,
          bytes: testWallet != "" ? testWallet.toBase58() : provider.wallet.publicKey.toBase58(),
        },
      },
    ])
    const currentSlot = await provider.connection.getSlot()
    const currentBlockTime = await provider.connection.getBlockTime(currentSlot)

    // used to get staked token info
    // await getAllInfo(provider, program, currentBlockTime);

    return Promise.all(
      accounts.map(async ({ account, publicKey }) => {
        const metadataAccount = await Metadata.getPDA(account.mint)
        const metadata = await Metadata.load(
          provider.connection,
          metadataAccount
        )
        let time = currentBlockTime - account.createdTimestamp.toNumber()
        const now = new Date().toLocaleString('en-US', { timeZone: 'UTC' })
        if (new Date(now).getTime() - time * 1000 < new Date("2/28/2022")) {
          time = (new Date(now).getTime() - new Date("2/1/2022")) / 1000
        }
        if ([392, 2291, 945, 2270, 4301, 4615, 4821, 4594].includes(parseInt(metadata.data.data.name.split("#").pop()))) {
          time = (new Date(now).getTime() - new Date("9/8/2022")) / 1000
        }
        if (metadata.data.data.symbol === "HVORIGINS") {
          return {
            id: Number(metadata.data.data.name.split("#").pop()),
            stakeAccount: publicKey,
            time,
            ...account,
          }
        }
      })
    )
  } catch (err) {
    console.log("Failed to get program accounts: ", err)
    return []
  }
}

const getAllInfo = async (provider, program, currentBlockTime) => {
  try {
    const accounts = await program.account.stakeItem.all()
    var tokenInfo = [];
    await Promise.all(
      accounts.map(async ({ account, publicKey }) => {
        const id = Object.keys(tokenMap).filter((key) => tokenMap[key] == account.mint.toBase58())[0];
        
        console.log('Loading tokens...')

        let time = currentBlockTime - account.createdTimestamp.toNumber()
        
        const now = new Date().toLocaleString('en-US', { timeZone: 'UTC' })
        if (new Date(now).getTime() - time * 1000 < new Date("2/28/2022")) {
          time = (new Date(now).getTime() - new Date("2/1/2022")) / 1000
        }
        if ([392, 2291, 945, 2270, 4301, 4615, 4821, 4594].includes(parseInt(id))) {
          time = (new Date(now).getTime() - new Date("9/8/2022")) / 1000
        }

        const data = {
          owner: account.authority.toBase58(),
          id: id,
          stakeAccount: publicKey,
          time,
          ...account,
        }
        tokenInfo.push(data)
      })
    );

    const stakedToken = tokenInfo.reduce((a, b) => {
      var i = a.findIndex(x => x.owner === b.owner);
      return i === -1 ?
        a.push({ 
          owner: b.owner, 
          info: [b] 
        }) :
        ( a[i].info.push(b)), a;
    }, []);

    await getBonusInfo(provider, stakedToken)

  } catch (error) {
    console.log('error', error);
  }
}

const getBonusInfo = async (provider, stakedToken) => {
  var tokenInfo = [];

  await Promise.all(
    stakedToken.map(async (item, index) => {
      const [
        isHeroPassholder,
        isVillainPassholder,
        ddStakedCount,
        stakedNFTs,
        stakedHeroNFTs,
        stakedVillainNFTs,
        bonusInfo,
        remains
      ] = await useStakedBonus(provider, item.owner, item.info)

      console.log('Loading bonus...')

      const isSeeker = seekerAddresses.some(address => address === item.owner)

      let hero_bonus = calcStakedBonus(
        stakedNFTs,
        stakedHeroNFTs,
        isHeroPassholder,
        ddStakedCount > 0,
        10,
        remains ? remains.hero : 0,
        isSeeker
      )

      let villian_bonus = calcStakedBonus(
        stakedNFTs,
        stakedVillainNFTs,
        isVillainPassholder,
        ddStakedCount > 0,
        10,
        remains ? remains.villain : 0,
        isSeeker 
      )
      
      tokenInfo.push({
        'owner': item.owner,
        'total': item.info.length + ' staked',
        'hero': Math.floor(hero_bonus), 
        'villian': Math.floor(villian_bonus)
      })
    })
  )

  console.log('debug', tokenInfo)
}

export const getBonus = (
  provider,
  nfts,
  heroPrice,
  villainPrice,
  heroTime,
  villainTime
) => {
  let baseBonus = 0
  let heroBonus = 0
  let villainBonus = 0
  let totalBonus = 0
  let racialBonusFlag = false
  // let currentSlot = await provider.connection.getSlot()
  // let currentBlockTime = await provider.connection.getBlockTime(currentSlot)
  let raceBonus = {
    time: 0,
    active: true,
    data: {
      human: {
        isStaked: false,
        time: 0,
        image: "ra_human.png",
      },
      elf: {
        isStaked: false,
        time: 0,
        image: "ra_elf.png",
      },
      demon: {
        isStaked: false,
        time: 0,
        image: "ra_demon.png",
      },
      alien: {
        isStaked: false,
        time: 0,
        image: "ra_alien.png",
      },
      merfolk: {
        isStaked: false,
        time: 0,
        image: "ra_merfolk.png",
      },
      celestial: {
        isStaked: false,
        time: 0,
        image: "ra_celestial.png",
      },
    },
  }
  let dualityBonus = {
    time: 0,
    active: true,
    data: {
      hero: {
        isStaked: false,
        time: 0,
        image: "du_hero.png",
      },
      villain: {
        isStaked: false,
        time: 0,
        image: "du_villain.png",
      },
    },
  }
  let bgBonus = {
    time: 0,
    active: true,
    data: {
      the_moon: {
        isStaked: false,
        time: 0,
        image: "bg_the_moon.png",
      },
      abandoned_factory: {
        isStaked: false,
        time: 0,
        image: "bg_abandoned_factory.png",
      },
      city_rooftop: {
        isStaked: false,
        time: 0,
        image: "bg_city_rooftop.png",
      },
      desolate_monastery: {
        isStaked: false,
        time: 0,
        image: "bg_desolate_monastery.png",
      },
      drakkar_shipwreck: {
        isStaked: false,
        time: 0,
        image: "bg_drakkar_shipwreck.png",
      },
      egyptian_tomb: {
        isStaked: false,
        time: 0,
        image: "bg_egyptian_tomb.png",
      },
      government_lab: {
        isStaked: false,
        time: 0,
        image: "bg_government_lab.png",
      },
      japanese_shrine: {
        isStaked: false,
        time: 0,
        image: "bg_japanese_shrine.png",
      },
      mayan_temple: {
        isStaked: false,
        time: 0,
        image: "bg_mayan_temple.png",
      },
      meteor_shower: {
        isStaked: false,
        time: 0,
        image: "bg_meteor_shower.png",
      },
      mount_olympus: {
        isStaked: false,
        time: 0,
        image: "bg_mount_olympus.png",
      },
      nuclear_power_plant: {
        isStaked: false,
        time: 0,
        image: "bg_nuclear_power_plant.png",
      },
      prison_cell: {
        isStaked: false,
        time: 0,
        image: "bg_prison_cell.png",
      },
      seaside_castle_ruins: {
        isStaked: false,
        time: 0,
        image: "bg_seaside_castle_ruins.png",
      },
      underworld_clinic: {
        isStaked: false,
        time: 0,
        image: "bg_underworld_clinic.png",
      },
    },
  }
  nfts.map(nft => {
    // let holdTime =  (currentBlockTime - nft.time) / 3600
    let holdTime = 2
    nft.metadata.attributes.map(attr => {
      if (attr.trait_type === "Alignment") {
        if (attr.value === "Hero") {
          heroBonus += heroPrice * holdTime
          if (!dualityBonus.data.hero.isStaked) {
            dualityBonus.data.hero.isStaked = true
            dualityBonus.data.hero.time = nft.time
          }
        }
        if (attr.value === "Villain") {
          villainBonus += villainPrice * holdTime
          if (!dualityBonus.data.villain.isStaked) {
            dualityBonus.data.villain.isStaked = true
            dualityBonus.data.villain.time = nft.time
          }
        }
      }

      if (attr.trait_type === "Race") {
        switch (attr.value) {
          case "Alien":
            if (!raceBonus.data.alien.isStaked) {
              raceBonus.data.alien.isStaked = true
              raceBonus.data.alien.time = nft.time
            }
            break
          case "The Greys":
            if (!raceBonus.data.alien.isStaked) {
              raceBonus.data.alien.isStaked = true
              raceBonus.data.alien.time = nft.time
            }
            break
          case "The Reptilians":
            if (!raceBonus.data.alien.isStaked) {
              raceBonus.data.alien.isStaked = true
              raceBonus.data.alien.time = nft.time
            }
            break
          case "Elf":
            if (!raceBonus.data.elf.isStaked) {
              raceBonus.data.elf.isStaked = true
              raceBonus.data.elf.time = nft.time
            }
            break
          case "Light Elf":
            if (!raceBonus.data.elf.isStaked) {
              raceBonus.data.elf.isStaked = true
              raceBonus.data.elf.time = nft.time
            }
            break
          case "Dark Elf":
            if (!raceBonus.data.elf.isStaked) {
              raceBonus.data.elf.isStaked = true
              raceBonus.data.elf.time = nft.time
            }
            break
          case "Demon":
            if (!raceBonus.data.demon.isStaked) {
              raceBonus.data.demon.isStaked = true
              raceBonus.data.demon.time = nft.time
            }
            break
          case "Archdemon":
            if (!raceBonus.data.demon.isStaked) {
              raceBonus.data.demon.isStaked = true
              raceBonus.data.demon.time = nft.time
            }
            break
          case "The Ascended":
            if (!raceBonus.data.demon.isStaked) {
              raceBonus.data.demon.isStaked = true
              raceBonus.data.demon.time = nft.time
            }
            break
          case "Celestial":
            if (!raceBonus.data.celestial.isStaked) {
              raceBonus.data.celestial.isStaked = true
              raceBonus.data.celestial.time = nft.time
            }
            break
          case "The Fallen":
            if (!raceBonus.data.celestial.isStaked) {
              raceBonus.data.celestial.isStaked = true
              raceBonus.data.celestial.time = nft.time
            }
            break
          case "Seraphim":
            if (!raceBonus.data.celestial.isStaked) {
              raceBonus.data.celestial.isStaked = true
              raceBonus.data.celestial.time = nft.time
            }
            break
          case "Human":
            if (!raceBonus.data.human.isStaked) {
              raceBonus.data.human.isStaked = true
              raceBonus.data.human.time = nft.time
            }
            break
          case "Merfolk":
            if (!raceBonus.data.merfolk.isStaked) {
              raceBonus.data.merfolk.isStaked = true
              raceBonus.data.merfolk.time = nft.time
            }
            break
          case "Atlantean":
            if (!raceBonus.data.merfolk.isStaked) {
              raceBonus.data.merfolk.isStaked = true
              raceBonus.data.merfolk.time = nft.time
            }
            break
          case "Darkwater Merfolk":
            if (!raceBonus.data.merfolk.isStaked) {
              raceBonus.data.merfolk.isStaked = true
              raceBonus.data.merfolk.time = nft.time
            }
            break
          default:
            break
        }
      }

      if (attr.trait_type === "Background") {
        switch (attr.value) {
          case "Abandoned Factory":
            if (!bgBonus.data.abandoned_factory.isStaked) {
              bgBonus.data.abandoned_factory.isStaked = true
              bgBonus.data.abandoned_factory.time = nft.time
            }
            break
          case "City Rooftop":
            if (!bgBonus.data.city_rooftop.isStaked) {
              bgBonus.data.city_rooftop.isStaked = true
              bgBonus.data.city_rooftop.time = nft.time
            }
            break
          case "Desolate Monastery":
            if (!bgBonus.data.desolate_monastery.isStaked) {
              bgBonus.data.desolate_monastery.isStaked = true
              bgBonus.data.desolate_monastery.time = nft.time
            }
            break
          case "Drakkar Shipwreck":
            if (!bgBonus.data.drakkar_shipwreck.isStaked) {
              bgBonus.data.drakkar_shipwreck.isStaked = true
              bgBonus.data.drakkar_shipwreck.time = nft.time
            }
            break
          case "Egyptian Tomb":
            if (!bgBonus.data.egyptian_tomb.isStaked) {
              bgBonus.data.egyptian_tomb.isStaked = true
              bgBonus.data.egyptian_tomb.time = nft.time
            }
            break
          case "Government Lab":
            if (!bgBonus.data.government_lab.isStaked) {
              bgBonus.data.government_lab.isStaked = true
              bgBonus.data.government_lab.time = nft.time
            }
            break
          case "Japanese Shrine":
            if (!bgBonus.data.japanese_shrine.isStaked) {
              bgBonus.data.japanese_shrine.isStaked = true
              bgBonus.data.japanese_shrine.time = nft.time
            }
            break
          case "Mayan Temple":
            if (!bgBonus.data.mayan_temple.isStaked) {
              bgBonus.data.mayan_temple.isStaked = true
              bgBonus.data.mayan_temple.time = nft.time
            }
            break
          case "Meteor Shower":
            if (!bgBonus.data.meteor_shower.isStaked) {
              bgBonus.data.meteor_shower.isStaked = true
              bgBonus.data.meteor_shower.time = nft.time
            }
            break
          case "Mount Olympus":
            if (!bgBonus.data.mount_olympus.isStaked) {
              bgBonus.data.mount_olympus.isStaked = true
              bgBonus.data.mount_olympus.time = nft.time
            }
            break
          case "Nuclear Power Plant":
            if (!bgBonus.data.nuclear_power_plant.isStaked) {
              bgBonus.data.nuclear_power_plant.isStaked = true
              bgBonus.data.nuclear_power_plant.time = nft.time
            }
            break
          case "Prison Cell":
            if (!bgBonus.data.prison_cell.isStaked) {
              bgBonus.data.prison_cell.isStaked = true
              bgBonus.data.prison_cell.time = nft.time
            }
            break
          case "Seaside Castle Ruins":
            if (!bgBonus.data.seaside_castle_ruins.isStaked) {
              bgBonus.data.seaside_castle_ruins.isStaked = true
              bgBonus.data.seaside_castle_ruins.time = nft.time
            }
            break
          case "The Moon":
            if (!bgBonus.data.the_moon.isStaked) {
              bgBonus.data.the_moon.isStaked = true
              bgBonus.data.the_moon.time = nft.time
            }
            break
          case "Underworld Clinic":
            if (!bgBonus.data.underworld_clinic.isStaked) {
              bgBonus.data.underworld_clinic.isStaked = true
              bgBonus.data.underworld_clinic.time = nft.time
            }
            break
          default:
            break
        }
        if (attr.value === "Hero") heroBonus += heroPrice * holdTime
        if (attr.value === "Villain") villainBonus += villainPrice * holdTime
      }
    })
  })

  // Racial Equality case
  if (racialBonusFlag) totalBonus = totalBonus + baseBonus * 0.25

  // Duality
  if (heroBonus > 0 && villainBonus > 0)
    totalBonus = totalBonus + baseBonus * 0.1

  // The More, The Merrier case
  if (nfts.length > 1) totalBonus = totalBonus + baseBonus * 0.05

  Object.keys(raceBonus.data).map(key => {
    let active = raceBonus.data[key].isStaked
    raceBonus.time = raceBonus.data[key].time
    if (!active) {
      raceBonus.active = false
      return
    }
  })

  Object.keys(dualityBonus.data).map(key => {
    let active = dualityBonus.data[key].isStaked
    dualityBonus.time = dualityBonus.data[key].time
    if (!active) {
      dualityBonus.active = false
      return
    }
  })

  Object.keys(bgBonus.data).map(key => {
    let active = bgBonus.data[key].isStaked
    bgBonus.time = bgBonus.data[key].time
    if (!active) {
      bgBonus.active = false
      return
    }
  })

  return {
    race: raceBonus,
    duality: dualityBonus,
    background: bgBonus,
  }
}

export const getDefaultBonus = () => {
  let raceBonus = {
    time: 0,
    active: false,
    data: {
      human: {
        isStaked: false,
        time: 0,
        image: "ra_human.png",
      },
      elf: {
        isStaked: false,
        time: 0,
        image: "ra_elf.png",
      },
      demon: {
        isStaked: false,
        time: 0,
        image: "ra_demon.png",
      },
      alien: {
        isStaked: false,
        time: 0,
        image: "ra_alien.png",
      },
      merfolk: {
        isStaked: false,
        time: 0,
        image: "ra_merfolk.png",
      },
      celestial: {
        isStaked: false,
        time: 0,
        image: "ra_celestial.png",
      },
    },
  }
  let dualityBonus = {
    time: 0,
    active: false,
    data: {
      hero: {
        isStaked: false,
        time: 0,
        image: "du_hero.png",
      },
      villain: {
        isStaked: false,
        time: 0,
        image: "du_villain.png",
      },
    },
  }
  let bgBonus = {
    time: 0,
    active: false,
    data: {
      the_moon: {
        isStaked: false,
        time: 0,
        image: "bg_the_moon.png",
      },
      abandoned_factory: {
        isStaked: false,
        time: 0,
        image: "bg_abandoned_factory.png",
      },
      city_rooftop: {
        isStaked: false,
        time: 0,
        image: "bg_city_rooftop.png",
      },
      desolate_monastery: {
        isStaked: false,
        time: 0,
        image: "bg_desolate_monastery.png",
      },
      drakkar_shipwreck: {
        isStaked: false,
        time: 0,
        image: "bg_drakkar_shipwreck.png",
      },
      egyptian_tomb: {
        isStaked: false,
        time: 0,
        image: "bg_egyptian_tomb.png",
      },
      government_lab: {
        isStaked: false,
        time: 0,
        image: "bg_government_lab.png",
      },
      japanese_shrine: {
        isStaked: false,
        time: 0,
        image: "bg_japanese_shrine.png",
      },
      mayan_temple: {
        isStaked: false,
        time: 0,
        image: "bg_mayan_temple.png",
      },
      meteor_shower: {
        isStaked: false,
        time: 0,
        image: "bg_meteor_shower.png",
      },
      mount_olympus: {
        isStaked: false,
        time: 0,
        image: "bg_mount_olympus.png",
      },
      nuclear_power_plant: {
        isStaked: false,
        time: 0,
        image: "bg_nuclear_power_plant.png",
      },
      prison_cell: {
        isStaked: false,
        time: 0,
        image: "bg_prison_cell.png",
      },
      seaside_castle_ruins: {
        isStaked: false,
        time: 0,
        image: "bg_seaside_castle_ruins.png",
      },
      underworld_clinic: {
        isStaked: false,
        time: 0,
        image: "bg_underworld_clinic.png",
      },
    },
  }

  return {
    race: raceBonus,
    duality: dualityBonus,
    background: bgBonus,
  }
}

/**
 *
 * @param {*} totalStakedCount
 * total count of staked NFTs(both of Hero, Villain)
 * @param {*} baseRatePerUnit
 * base rate per nft(hero or villian)
 * @param {*} stakedNfts
 * list of staked nfts
 * @param {*} isPassholder
 * boolean to check whether it's passholder(HeroPass or VillainPass)
 * @param {*} isDDStaked
 * boolean to check whether user has dapper duck nft(in his wallet or staked)
 */
export const calcBonus = (
  isHero,
  walletAddress,
  totalStakedNfts,
  stakedNfts,
  isPassholder,
  hasDDOrStaked,
  baseRatePerUnit = 10,
  remain,
  isSeeker
) => {
  const bonusInfo = getBonus(null, totalStakedNfts, baseRatePerUnit)
  let preStakedNfts = []
  let overStakedNfts = []
  // The staked amount over to August 7th UTC
  stakedNfts.map((item) => {
    if (item.createdTimestamp.toNumber() * 1000 >= new Date("8/7/2022")) {
      overStakedNfts.push(item)
    } else {
      preStakedNfts.push(item)
    }
  }
  )

  let overAmount = 0
  totalStakedNfts.map((item) => {
    if (item.createdTimestamp.toNumber() * 1000 >= new Date("8/7/2022")) {
      overAmount++
    }
  }
  )

  let mul = 0;
  if (overAmount > 100) {
    mul = 50;
  } else if (overAmount > 50) {
    mul = 25;
  } else if (overAmount > 0) {
    mul = 10;
  }

  let overBaseBonus = getBonusCalc(
    bonusInfo,
    overStakedNfts,
    isPassholder,
    isSeeker,
    hasDDOrStaked,
    baseRatePerUnit,
    false
  );

  let preBaseBonus = getBonusCalc(
    bonusInfo,
    preStakedNfts,
    isPassholder,
    isSeeker,
    hasDDOrStaked,
    baseRatePerUnit,
    false
  );

  // The more, The Merrier: +5% on their Base Rate per NFT staked, for every NFT beyond the first staked and 7th: 0-50 NFTs = 10X; 51-100 NFTs = 25X; 101+ NFTs = 50X multiplier.

  overBaseBonus *= mul
  preBaseBonus *= Math.pow(1.05, totalStakedNfts.length - overAmount)

  console.log(
    `The More, the merrier (*pow(1.05, ${totalStakedNfts.length - overAmount}) = ${preBaseBonus}) + (over August 7th Amount: ${overAmount} = ${overBaseBonus}) : `,
    preBaseBonus + overBaseBonus
  )

  const stakedBonus = stakedTokenInfo.filter((item) => { return item.owner == walletAddress })[0]

  const bonus = preBaseBonus + overBaseBonus - remain + (isHero ? stakedBonus?.hero : stakedBonus?.villian)
  
  // const bonus = preBaseBonus + overBaseBonus - remain

  return new Intl.NumberFormat("en-US", { style: "decimal" }).format(
    Math.floor(bonus)
  )
}


export const calcStakedBonus = (
  totalStakedNfts,
  stakedNfts,
  isPassholder,
  hasDDOrStaked,
  baseRatePerUnit = 10,
  remain,
  isSeeker
) => {
  const bonusInfo = getBonus(null, totalStakedNfts, baseRatePerUnit)
  let preStakedNfts = []
  let overStakedNfts = []
  stakedNfts.map((item) => {
    if (item.createdTimestamp.toNumber() * 1000 >= new Date("8/7/2022")) {
      overStakedNfts.push(item)
    } else {
      preStakedNfts.push(item)
    }
  }
  )

  let overAmount = 0
  totalStakedNfts.map((item) => {
    if (item.createdTimestamp.toNumber() * 1000 >= new Date("8/7/2022")) {
      overAmount++
    }
  }
  )

  let mul = 0;
  if (overAmount > 100) {
    mul = 50;
  } else if (overAmount > 50) {
    mul = 25;
  } else if (overAmount > 0) {
    mul = 10;
  }

  let overBaseBonus = getBonusCalc(
    bonusInfo,
    overStakedNfts,
    isPassholder,
    isSeeker,
    hasDDOrStaked,
    baseRatePerUnit,
    true
  );

  let preBaseBonus = getBonusCalc(
    bonusInfo,
    preStakedNfts,
    isPassholder,
    isSeeker,
    hasDDOrStaked,
    baseRatePerUnit,
    true
  );

  overBaseBonus *= mul
  preBaseBonus *= Math.pow(1.05, totalStakedNfts.length - overAmount)

  console.log(
    `The More, the merrier (*pow(1.05, ${totalStakedNfts.length - overAmount}) = ${preBaseBonus}) + (over August 7th Amount: ${overAmount} = ${overBaseBonus}) : `,
    preBaseBonus + overBaseBonus
  )

  const bonus = preBaseBonus + overBaseBonus - remain

  return bonus;
}

const getBonusCalc = (
  bonusInfo,
  stakedNfts,
  isPassholder,
  isSeeker,
  hasDDOrStaked,
  baseRatePerUnit,
  isStakedInfo
) => {
  const baseEarning = stakedNfts.reduce(
    (pv, cv) => pv + modifyBaseRate(cv.time, baseRatePerUnit, isStakedInfo),
    0
  )
  console.log("Base: ", baseEarning)
  let bonus = baseEarning

  // Passholders: 2X on their $HERO Base Rate if they hold a HeroPass, 2X on their $VILLAIN Base Rate if they hold a VillainPass
  if (isPassholder) {
    bonus *= 2
    console.log("Passholder(*2): ", bonus)
  }
  if (isSeeker || bonusInfo.background.active) {
    bonus *= 1.75
    console.log("Seeker(*1.75): ", bonus)
  }
  if (bonusInfo.race.active) {
    bonus *= 1.25
    console.log("Racial(*1.25): ", bonus)
  }
  if (bonusInfo.duality.active) {
    bonus *= 1.1
    console.log("Duality(*1.1): ", bonus)
  }
  if (hasDDOrStaked) {
    bonus *= 1.1
    console.log("Dapper duck staked(*1.1): ", bonus)
  }

  return bonus;
}

const modifyBaseRate = (time, rate, isStakedInfo) => {
  const modify_date = new Date("1/6/2023").toLocaleString('en-US', { timeZone: 'UTC' });
  const modify_time = new Date(modify_date).getTime() / 1000;
  const current_date = new Date().toLocaleString('en-US', { timeZone: 'UTC' })
  const current_time = new Date(current_date).getTime() / 1000;
  const diff_time = current_time - modify_time;

  if(isStakedInfo)
    return (Math.floor((time - diff_time) / 3600) + 1) * rate

  /**
   * This will be used to get base bonus till 1/6/2023,
   */
  // if (time < diff_time) {
  //   return 0;
  // } else {
  //   return (Math.floor((time - diff_time) / 3600) + 1) * rate;
  // }

  
  if (time < diff_time) {
    return (Math.floor(time / 3600) + 1) * rate * 0.05;
  } else {
    return (Math.floor(diff_time / 3600) + 1) * rate * 0.05;
  }
}
